import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, WhatsappLogo, Minus, Plus } from '@phosphor-icons/react';
import { formatPrice, generateWhatsAppURL } from '../utils/helpers';
import { useCartStore } from '../store';
import { toast } from 'sonner';

const ProductQuickView = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = React.useState(1);
  const addItem = useCartStore((state) => state.addItem);

  React.useEffect(() => {
    if (isOpen) setQuantity(1);
  }, [isOpen]);

  if (!product) return null;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addItem(product);
    }
    toast.success(`${product.name} added to cart`);
    onClose();
  };

  const handleWhatsApp = () => {
    const url = generateWhatsAppURL([{ ...product, quantity }], product.selling_rate * quantity);
    window.open(url, '_blank');
  };

  const images = product.image_urls?.length > 0 
    ? product.image_urls 
    : ['https://images.unsplash.com/photo-1736436789706-005f2218a96d?w=600'];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="relative bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            >
              <X size={20} className="text-secondary" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Image */}
              <div className="aspect-square md:aspect-auto md:h-full bg-background-subtle">
                <img
                  src={images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Details */}
              <div className="p-6 md:p-8 flex flex-col">
                {/* Category */}
                {product.category_name && (
                  <span className="font-body text-xs text-primary uppercase tracking-widest mb-2">
                    {product.category_name}
                  </span>
                )}

                {/* Name */}
                <h2 className="font-display text-2xl md:text-3xl text-secondary mb-3">
                  {product.name}
                </h2>

                {/* Price */}
                <p className="font-display text-2xl text-primary mb-2">
                  {formatPrice(product.selling_rate)}
                  {product.mrp && product.mrp > product.selling_rate && (
                    <span className="text-text-muted line-through ml-2 text-lg font-normal">
                      {formatPrice(product.mrp)}
                    </span>
                  )}
                </p>
                {product.mrp && product.mrp > product.selling_rate && (
                  <p className="font-body text-sm text-green-600 mb-2">
                    Save {Math.round((1 - product.selling_rate / product.mrp) * 100)}% off
                  </p>
                )}
                <p className="font-body text-sm text-green-600 mb-4">
                  Free Shipping
                </p>

                {/* Description */}
                {product.description && (
                  <div className="font-body text-sm text-text-secondary leading-relaxed mb-4">
                    {product.description.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-2">
                        {paragraph.split('"').map((part, partIdx) => 
                          partIdx % 2 === 1 ? (
                            <span key={partIdx} className="italic text-primary font-medium">"{part}"</span>
                          ) : (
                            part
                          )
                        )}
                      </p>
                    ))}
                  </div>
                )}
                
                {/* Product Details */}
                {(product.type || product.design || product.material || product.closure_type) && (
                  <div className="mb-6 p-4 bg-background-subtle rounded-lg">
                    <h3 className="font-display text-sm text-secondary mb-3">Product Details</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {product.type && (
                        <div>
                          <p className="font-body text-xs text-text-muted uppercase">Type</p>
                          <p className="font-body text-sm text-secondary">{product.type}</p>
                        </div>
                      )}
                      {product.design && (
                        <div>
                          <p className="font-body text-xs text-text-muted uppercase">Design</p>
                          <p className="font-body text-sm text-secondary">{product.design}</p>
                        </div>
                      )}
                      {product.material && (
                        <div>
                          <p className="font-body text-xs text-text-muted uppercase">Material</p>
                          <p className="font-body text-sm text-secondary">{product.material}</p>
                        </div>
                      )}
                      {product.closure_type && (
                        <div>
                          <p className="font-body text-xs text-text-muted uppercase">Closure</p>
                          <p className="font-body text-sm text-secondary">{product.closure_type}</p>
                        </div>
                      )}
                    </div>
                    {product.styling_tip && (
                      <div className="mt-3 pt-3 border-t border-border-default">
                        <p className="font-body text-xs text-text-muted uppercase mb-1">Styling Tip</p>
                        <p className="font-body text-sm text-secondary italic">{product.styling_tip}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Stock Status */}
                <div className="mb-6">
                  {(product.stock_quantity ?? product.quantity ?? 0) > 0 ? (
                    <span className="font-body text-sm text-green-600">
                      ✓ In Stock {(product.stock_quantity ?? product.quantity ?? 0) < 5 && `(Only ${product.stock_quantity ?? product.quantity ?? 0} left)`}
                    </span>
                  ) : (
                    <span className="font-body text-sm text-red-500">Out of Stock</span>
                  )}
                </div>

                {/* Quantity */}
                {(product.stock_quantity ?? product.quantity ?? 0) > 0 && (
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-body text-sm text-text-secondary">Quantity:</span>
                    <div className="flex items-center border border-border-default">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="p-2 hover:bg-background-subtle transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="px-4 font-body">{quantity}</span>
                      <button
                        onClick={() => setQuantity(Math.min((product.stock_quantity ?? product.quantity ?? 0), quantity + 1))}
                        className="p-2 hover:bg-background-subtle transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-auto space-y-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={(product.stock_quantity ?? product.quantity ?? 0) === 0}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShoppingBag size={20} />
                    Add to Cart
                  </button>
                  <button
                    onClick={handleWhatsApp}
                    className="whatsapp-btn w-full justify-center"
                  >
                    <WhatsappLogo size={20} weight="fill" />
                    Buy on WhatsApp
                  </button>
                </div>

                {/* Features */}
                <div className="mt-6 pt-6 border-t border-border-default grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-background-subtle">
                    <p className="font-body text-xs text-text-muted">Free Shipping</p>
                    <p className="font-body text-sm text-secondary">Orders ₹999+</p>
                  </div>
                  <div className="text-center p-3 bg-background-subtle">
                    <p className="font-body text-xs text-text-muted">Easy Returns</p>
                    <p className="font-body text-sm text-secondary">7 Days</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProductQuickView;
